import { queryOptions } from '@tanstack/react-query'
import { apiGet, unwrap } from '@/shared/api'
import type { Board } from '../model/schema'
import { type BoardDto, toBoard } from './mappers'

export const boardKeys = {
  all: ['boards'] as const,
  detail: (id: string) => [...boardKeys.all, 'detail', id] as const,
  list: (projectId: string) => [...boardKeys.all, 'list', projectId] as const,
  companyList: (companyId: string) => [...boardKeys.all, 'company', companyId] as const,
  personal: (employeeId: string) => [...boardKeys.all, 'personal', employeeId] as const,
}

export function boardQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: boardKeys.detail(boardId),
    queryFn: async (): Promise<Board> =>
      toBoard(unwrap<BoardDto>(await apiGet(`/boards/${boardId}`))),
  })
}

// Доски проекта с колонками (GET /projects/{id}/boards, sort по department как бэк).
// Питает хаб /boards: мини-сводки колонок без N detail-запросов.
export function boardListQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: boardKeys.list(projectId),
    queryFn: async (): Promise<Board[]> => {
      const list = unwrap<BoardDto[]>(await apiGet(`/projects/${projectId}/boards`)) ?? []
      return list.map(toBoard)
    },
  })
}

// Доски самой компании — работа её уровня (договор, бюджет, онбординг), которой
// не нужен проект. Доски проектов компании сюда не входят: они читаются по своим
// проектам и живут в карточках проектов.
export function companyBoardListQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: boardKeys.companyList(companyId),
    queryFn: async (): Promise<Board[]> => {
      const list = unwrap<BoardDto[]>(await apiGet(`/companies/${companyId}/boards`)) ?? []
      return list.map(toBoard)
    },
  })
}

// Личная доска сотрудника. Бэкенд заводит её при первом обращении, поэтому
// запрос всегда возвращает доску, а не 404 для тех, кто её ещё не открывал.
// employeeId не передан — своя доска (`/employees/me/board`).
export function personalBoardQueryOptions(employeeId?: string) {
  const path = employeeId ? `/employees/${employeeId}/board` : '/employees/me/board'
  return queryOptions({
    queryKey: boardKeys.personal(employeeId ?? 'me'),
    queryFn: async (): Promise<Board> => toBoard(unwrap<BoardDto>(await apiGet(path))),
  })
}
